import axios, { AxiosRequestHeaders } from "axios";
import { CapacitorHttp, HttpOptions } from "@capacitor/core";
import { getAuth } from "firebase/auth";
import { getApps, getApp } from "firebase/app";

import * as capacitorStorage from "./localStorage";
import { LOCAL_STORAGE } from "../config";
import { isMobile } from "./common";
import { waitForAPIGate } from "./apiGate";

export type WLCommonPromiseReturn = {
  data: string;
};
type WLHeaders = HttpOptions["headers"] | AxiosRequestHeaders;
type WLBody = {
  [key: string]: any;
};
const HTTP_STATUS = {
  ok: 200,
  created: 201,
  noContent: 204, // Often used for successful PUT or DELETE operations
  badRequest: 400, // Bad request error
  unauthorized: 401, // Authentication has not yet been provided or is failed
  forbidden: 403, // No Permission
  notFound: 404, // Resource not found
  internalServerError: 500, // Generic error message, a server-side error
};

type HttpErrorHandler = (response: { status: number; data: unknown }) => void;

let httpErrorHandler: HttpErrorHandler | null = null;

export const setHttpErrorHandler = (handler: HttpErrorHandler | null): void => {
  httpErrorHandler = handler;
};

export const getRequestHeaders = async (authenticated = false) => {
  let headers = {
    "Content-Type": "application/json",
  };
  if (authenticated) {
    // Always get token directly from Firebase instead of localStorage
    // Firebase's getIdToken() automatically refreshes if expired
    // This is the root fix - trust Firebase's built-in token management
    let id_token = "";
    let user_id = "";

    // Use Firebase's getIdToken() for ALL platforms (web and mobile)
    // This ensures fresh tokens even after app returns from long background
    // Firebase SDK handles caching internally - getIdToken(false) uses cache if valid
    try {
      // Handle named Firebase apps (admin, appuser, webuser)
      // Each platform initializes with a named app, so we try each one
      let auth;
      const platformNames = ["admin", "appuser", "webuser"];
      let foundApp = null;

      // Try to get app by name for each platform
      for (const platformName of platformNames) {
        try {
          const app = getApp(platformName);
          foundApp = app;
          break;
        } catch (error) {
          // This platform's app doesn't exist, try next one
          continue;
        }
      }

      if (foundApp) {
        try {
          auth = getAuth(foundApp);
        } catch (authError) {
          // If getAuth fails, try fallback
          const apps = getApps();
          if (apps.length > 0) {
            auth = getAuth(apps[0]);
          } else {
            throw new Error("No Firebase app initialized");
          }
        }
      } else {
        // Fallback: try to get any initialized app
        const apps = getApps();
        if (apps.length > 0) {
          auth = getAuth(apps[0]);
        } else {
          throw new Error("No Firebase app initialized");
        }
      }

      await auth.authStateReady();

      // Capture currentUser reference to avoid race conditions
      // auth.currentUser is mutable and could become null during async operations
      const currentUser = auth.currentUser;

      if (currentUser) {
        try {
          // Get token from Firebase (will auto-refresh if needed)
          // false = use cache if valid, Firebase SDK automatically refreshes ~5min before expiration
          id_token = await currentUser.getIdToken(false);
          user_id = currentUser.uid;

          // Update localStorage cache for backwards compatibility
          await capacitorStorage.setItem(LOCAL_STORAGE.fbIdToken, id_token);
          await capacitorStorage.setItem(LOCAL_STORAGE.userId, user_id);
        } catch (error) {
          // If token fetch fails, try force refresh once
          // Re-check if user is still valid before retrying
          // User could have logged out during the async getIdToken call
          if (auth.currentUser && auth.currentUser.uid === currentUser.uid) {
            try {
              id_token = await auth.currentUser.getIdToken(true);
              user_id = auth.currentUser.uid;
              await capacitorStorage.setItem(LOCAL_STORAGE.fbIdToken, id_token);
              await capacitorStorage.setItem(LOCAL_STORAGE.userId, user_id);
            } catch (refreshError) {
              console.error("Failed to get Firebase token:", refreshError);
              // Fallback to localStorage if Firebase fails
              id_token =
                (await capacitorStorage.getItem(LOCAL_STORAGE.fbIdToken)) || "";
              user_id =
                (await capacitorStorage.getItem(LOCAL_STORAGE.userId)) || "";
            }
          } else {
            // User logged out during token fetch - fallback to localStorage
            console.warn(
              "User logged out during token fetch, using localStorage fallback"
            );
            id_token =
              (await capacitorStorage.getItem(LOCAL_STORAGE.fbIdToken)) || "";
            user_id =
              (await capacitorStorage.getItem(LOCAL_STORAGE.userId)) || "";
          }
        }
      } else {
        // Fallback to localStorage if no current user
        id_token =
          (await capacitorStorage.getItem(LOCAL_STORAGE.fbIdToken)) || "";
        user_id = (await capacitorStorage.getItem(LOCAL_STORAGE.userId)) || "";
      }
    } catch (error) {
      // If auth instance not available, fallback to localStorage
      console.error("Failed to get Firebase auth:", error);
      id_token =
        (await capacitorStorage.getItem(LOCAL_STORAGE.fbIdToken)) || "";
      user_id = (await capacitorStorage.getItem(LOCAL_STORAGE.userId)) || "";
    }

    headers = {
      ...headers,
      ...{ id_token, user_id },
    };
  }
  return headers as WLHeaders;
};

export const encodeQueryData = (data: Record<string, unknown>) => {
  const pairs: string[] = [];

  Object.keys(data).forEach((key) => {
    const value = data[key];
    const encodedKey = isMobile() ? key : encodeURIComponent(key);

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry === undefined || entry === null) {
          return;
        }
        const encodedValue = isMobile()
          ? String(entry)
          : encodeURIComponent(String(entry));
        pairs.push(`${encodedKey}=${encodedValue}`);
      });
      return;
    }

    const encodedValue = isMobile() ? value : encodeURIComponent(String(value));
    pairs.push(`${encodedKey}=${encodedValue}`);
  });

  return pairs.join("&");
};

const pickCapacitorErrorMessage = (data: unknown): string => {
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  if (!data || typeof data !== "object") {
    return "Request failed";
  }

  const record = data as Record<string, unknown>;
  const nestedError = record.error;
  if (nestedError && typeof nestedError === "object") {
    const nested = nestedError as Record<string, unknown>;
    const action =
      typeof nested.action === "string" ? nested.action.trim() : "";
    const message =
      typeof nested.message === "string" ? nested.message.trim() : "";
    if (action || message) {
      return action || message;
    }
  }

  if (typeof record.message === "string" && record.message.trim()) {
    return record.message.trim();
  }
  if (typeof record.error === "string" && record.error.trim()) {
    return record.error.trim();
  }

  return "Request failed";
};

/**
 * CapacitorHttp resolves 4xx/5xx with a body instead of rejecting like axios.
 * Throw an axios-shaped error so shared parsers (getErrorMsg, isAxiosError) work on mobile.
 */
const throwCapacitorHttpError = (response: {
  status: number;
  data: unknown;
}): never => {
  const message = pickCapacitorErrorMessage(response.data);
  const error = new Error(message) as Error & {
    response: { status: number; data: unknown, };
    isAxiosError: true;
  };
  error.response = { status: response.status, data: response.data };
  error.isAxiosError = true;
  throw error;
};

const handleResponse = (response: any) => {
  // console.log("response --- ", JSON.stringify(response, null, 1));
  if (
    [
      HTTP_STATUS.noContent,
      HTTP_STATUS.badRequest,
      HTTP_STATUS.unauthorized,
      HTTP_STATUS.forbidden,
      HTTP_STATUS.notFound,
      HTTP_STATUS.internalServerError,
    ].indexOf(response.status) !== -1
  ) {
    httpErrorHandler?.({
      status: response.status,
      data: response.data,
    });
    throwCapacitorHttpError(response);
  } else return response;
};

export const doGet = async (httpUrl: string, headers: WLHeaders) => {
  await waitForAPIGate();
  // console.log("doGet -- ", httpUrl);
  return isMobile()
    ? CapacitorHttp.get({ url: encodeURI(httpUrl), headers }).then(
        (response) => {
          return handleResponse(response);
        }
      )
    : axios.get(httpUrl, {
        headers: headers,
      });
};

export const doPost = async (
  httpUrl: string,
  headers: WLHeaders,
  data: WLBody
) => {
  await waitForAPIGate();
  // console.log("doPost -- ", httpUrl);
  return isMobile()
    ? CapacitorHttp.post({
        url: encodeURI(httpUrl),
        headers,
        data,
      }).then((response) => {
        return handleResponse(response);
      })
    : axios.post(httpUrl, data, {
        headers: headers,
      });
};

export const doPatch = async (
  httpUrl: string,
  headers: WLHeaders,
  data: WLBody
) => {
  await waitForAPIGate();
  return isMobile()
    ? CapacitorHttp.patch({
        url: encodeURI(httpUrl),
        headers,
        data,
      }).then((response) => {
        return handleResponse(response);
      })
    : axios.patch(httpUrl, data, {
        headers: headers,
      });
};

export const doPut = async (
  httpUrl: string,
  headers: WLHeaders,
  data: WLBody
) => {
  await waitForAPIGate();
  return isMobile()
    ? CapacitorHttp.put({
        url: encodeURI(httpUrl),
        headers,
        data,
      }).then((response) => {
        return handleResponse(response);
      })
    : axios.put(httpUrl, data, {
        headers: headers,
      });
};

export const doDelete = async (httpUrl: string, headers: WLHeaders) => {
  await waitForAPIGate();
  return isMobile()
    ? CapacitorHttp.delete({
        url: encodeURI(httpUrl),
        headers,
      }).then((response) => {
        return handleResponse(response);
      })
    : axios.delete(httpUrl, {
        headers: headers,
      });
};
